import type {
  ManualBlock,
  ManualChapter,
  ManualChapterKey,
  ManualCondition,
  ManualSection,
} from './types';

/**
 * Reguły instrukcji obsługi (28.10.2026, S-28.10-02) — czyste funkcje bez
 * Reacta: co widzi użytkownik przy swoich przełącznikach, od którego rozdziału
 * zaczyna, wyszukiwarka. Plik importuje wyłącznie typy — korzysta z niego też
 * skrypt PDF (`e2e/manual/pdf.mjs`), więc PDF i ekran filtrują treść tak samo.
 */

/** Klucz przełącznika instrukcji (dane — jak w rejestrze API). */
export const MANUAL_FEATURE = 'userManual';

/** Kolejność rozdziałów — ekran i PDF. */
export const CHAPTER_ORDER: readonly ManualChapterKey[] = [
  'start',
  'planner',
  'ftlLead',
  'trade',
  'cs',
  'administration',
  'admin',
];

/**
 * Rozdział, który otwiera się na start — rola zalogowanego (identyfikatory
 * ról to dane). Użytkownik ogólny pracuje głównie w planowaniu FTL; gość
 * i rola nieznana (także własna z centrum ról) — „Pierwsze kroki”.
 */
const CHAPTER_BY_ROLE: Readonly<Record<string, ManualChapterKey>> = {
  PLANNER_FTL: 'planner',
  USER: 'planner',
  FTL_MASTER: 'ftlLead',
  TRADER: 'trade',
  TRADE_LEAD: 'trade',
  CS: 'cs',
  CS_LEAD: 'cs',
  ADMINISTRATION: 'administration',
  ADMINISTRATION_LEAD: 'administration',
  ADMIN: 'admin',
  COMPANY_ADMIN: 'admin',
};

export function chapterForRole(role: string | null | undefined): ManualChapterKey {
  if (!role || !Object.prototype.hasOwnProperty.call(CHAPTER_BY_ROLE, role)) return 'start';
  return CHAPTER_BY_ROLE[role];
}

export function isChapterKey(v: unknown): v is ManualChapterKey {
  return typeof v === 'string' && (CHAPTER_ORDER as readonly string[]).includes(v);
}

/** Czy warunek jest spełniony; brak warunku — zawsze. */
export function conditionMet(when: ManualCondition | undefined, isOn: (flag: string) => boolean): boolean {
  if (!when) return true;
  return (when.on ?? []).every((f) => isOn(f)) && (when.off ?? []).every((f) => !isOn(f));
}

function visibleBlock(block: ManualBlock, isOn: (flag: string) => boolean): ManualBlock | null {
  if (!conditionMet(block.when, isOn)) return null;
  if (block.kind === 'terms') {
    const items = block.items.filter((i) => conditionMet(i.when, isOn));
    return items.length > 0 ? { ...block, items } : null;
  }
  return block;
}

/** Sekcja z blokami widocznymi przy tych przełącznikach; pusta albo ukryta — `null`. */
export function visibleSection(section: ManualSection, isOn: (flag: string) => boolean): ManualSection | null {
  if (!conditionMet(section.when, isOn)) return null;
  const blocks = section.blocks
    .map((b) => visibleBlock(b, isOn))
    .filter((b): b is ManualBlock => b !== null);
  return blocks.length > 0 ? { ...section, blocks } : null;
}

/** Rozdział z widocznymi sekcjami (sekcje bez treści znikają). */
export function visibleChapter(chapter: ManualChapter, isOn: (flag: string) => boolean): ManualChapter {
  return {
    ...chapter,
    sections: chapter.sections
      .map((s) => visibleSection(s, isOn))
      .filter((s): s is ManualSection => s !== null),
  };
}

/** Rozdziały w kolejności instrukcji. */
export function orderedChapters(chapters: readonly ManualChapter[]): ManualChapter[] {
  return CHAPTER_ORDER.map((k) => chapters.find((c) => c.key === k)).filter(
    (c): c is ManualChapter => c !== undefined,
  );
}

/** Wszystkie teksty bloku — wyszukiwarka i test słów zakazanych. */
export function blockTexts(block: ManualBlock): string[] {
  switch (block.kind) {
    case 'text':
      return [block.text];
    case 'list':
      return [block.title ?? '', ...block.items];
    case 'task':
      return [block.title, block.intro ?? '', ...block.steps, block.outcome ?? ''];
    case 'terms':
      return [block.title, ...block.items.flatMap((i) => [i.term, i.meaning])];
    case 'note':
      return [block.text];
    case 'image':
      return [block.caption];
    default:
      return [];
  }
}

/** Teksty sekcji (z tytułem). */
export function sectionTexts(section: ManualSection): string[] {
  return [section.title, ...section.blocks.flatMap(blockTexts)].filter((t) => t.length > 0);
}

/** Litery bez polskich znaków i wielkości — „zlecen” znajduje „zleceń”. */
export function foldText(text: string): string {
  const map: Record<string, string> = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' };
  return text
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (c) => map[c] ?? c)
    .replace(/[„”"]/g, '');
}

export interface ManualHit {
  chapter: ManualChapterKey;
  chapterTitle: string;
  sectionId: string;
  sectionTitle: string;
  /** Fragment z trafieniem (do ~160 znaków). */
  snippet: string;
}

/**
 * Wyszukiwanie po tekście w widocznej treści: każde słowo zapytania musi
 * wystąpić w sekcji (bez polskich znaków i wielkości liter). Najpierw
 * trafienia w tytule sekcji. Zapytanie krótsze niż 2 znaki — brak wyników.
 */
export function searchManual(chapters: readonly ManualChapter[], query: string): ManualHit[] {
  const words = foldText(query)
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.join('').length < 2) return [];
  const hits: (ManualHit & { inTitle: boolean })[] = [];
  for (const chapter of chapters) {
    for (const section of chapter.sections) {
      const texts = sectionTexts(section);
      const all = foldText(texts.join(' \n '));
      if (!words.every((w) => all.includes(w))) continue;
      const first = texts.find((t) => foldText(t).includes(words[0])) ?? section.title;
      const at = Math.max(0, foldText(first).indexOf(words[0]) - 50);
      const snippet = `${at > 0 ? '…' : ''}${first.slice(at, at + 160)}${first.length > at + 160 ? '…' : ''}`;
      hits.push({
        chapter: chapter.key,
        chapterTitle: chapter.title,
        sectionId: section.id,
        sectionTitle: section.title,
        snippet,
        inTitle: words.every((w) => foldText(section.title).includes(w)),
      });
    }
  }
  return hits
    .sort((a, b) => Number(b.inTitle) - Number(a.inTitle))
    .map(({ inTitle, ...hit }) => hit);
}

/** Liczba zadań krok po kroku w rozdziałach (raport, PDF). */
export function taskCount(chapters: readonly ManualChapter[]): number {
  return chapters.reduce(
    (n, c) => n + c.sections.reduce((m, s) => m + s.blocks.filter((b) => b.kind === 'task').length, 0),
    0,
  );
}
